'use client';

import { c as _c } from "react/compiler-runtime";
import { Toast as ToastPrimitive } from '@base-ui/react/toast';
import { cn } from 'cn';
import { CircleCheckIcon, InfoIcon, OctagonXIcon, TriangleAlertIcon, XIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
export const toast = ToastPrimitive.createToastManager();
type ToastProviderProps = ToastPrimitive.Provider.Props;
export function ToastProvider(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <ToastPrimitive.Provider {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
type ToastPortalProps = ToastPrimitive.Portal.Props;
export function ToastPortal(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <ToastPrimitive.Portal data-slot="toast-portal" {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
type ToastViewportProps = ToastPrimitive.Viewport.Props;
export function ToastViewport(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("pointer-events-none fixed inset-x-4 bottom-4 z-50 mx-auto w-auto max-w-sm outline-none", "sm:right-4 sm:left-auto sm:mx-0 sm:w-full", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <ToastPrimitive.Viewport data-slot="toast-viewport" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
type ToastProps = ToastPrimitive.Root.Props;
export function Toast(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("group/toast pointer-events-auto absolute right-0 bottom-0 z-[calc(1000-var(--toast-index))] w-full origin-bottom rounded-2xl border bg-popover text-popover-foreground shadow-lg will-change-transform outline-none select-none", "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50", "[--gap:0.75rem] [--height:var(--toast-frontmost-height,var(--toast-height))] [--offset-y:calc(var(--toast-offset-y)*-1+calc(var(--toast-index)*var(--gap)*-1)+var(--toast-swipe-movement-y))] [--peek:0.75rem] [--scale:calc(max(0,1-(var(--toast-index)*0.1)))] [--shrink:calc(1-var(--scale))]", "h-(--height) transform-[translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)-(var(--toast-index)*var(--peek))-(var(--shrink)*var(--height))))_scale(var(--scale))]", "[transition:transform_500ms_cubic-bezier(0.22,1,0.36,1),opacity_500ms,height_150ms]", "after:absolute after:top-full after:left-0 after:h-[calc(var(--gap)+1px)] after:w-full after:content-['']", "data-expanded:h-(--toast-height) data-expanded:transform-[translateX(var(--toast-swipe-movement-x))_translateY(var(--offset-y))]", "data-limited:opacity-0 data-starting-style:transform-[translateY(150%)]", "[&[data-ending-style]:not([data-limited]):not([data-swipe-direction])]:transform-[translateY(150%)]", "data-ending-style:data-[swipe-direction=down]:transform-[translateY(calc(var(--toast-swipe-movement-y)+150%))]", "data-ending-style:data-[swipe-direction=left]:transform-[translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))]", "data-ending-style:data-[swipe-direction=right]:transform-[translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]", "data-ending-style:data-[swipe-direction=up]:transform-[translateY(calc(var(--toast-swipe-movement-y)-150%))]", "data-expanded:data-ending-style:data-[swipe-direction=down]:transform-[translateY(calc(var(--toast-swipe-movement-y)+150%))]", "data-expanded:data-ending-style:data-[swipe-direction=left]:transform-[translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))]", "data-expanded:data-ending-style:data-[swipe-direction=right]:transform-[translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]", "data-expanded:data-ending-style:data-[swipe-direction=up]:transform-[translateY(calc(var(--toast-swipe-movement-y)-150%))]", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <ToastPrimitive.Root data-slot="toast" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
type ToastContentProps = ToastPrimitive.Content.Props;
export function ToastContent(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("flex h-full items-center gap-3 overflow-hidden p-4 transition-opacity duration-250 ease-[cubic-bezier(0.22,1,0.36,1)]", "data-behind:opacity-0", "data-expanded:opacity-100", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <ToastPrimitive.Content data-slot="toast-content" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
type ToastTitleProps = ToastPrimitive.Title.Props;
export function ToastTitle(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("text-sm font-medium", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <ToastPrimitive.Title data-slot="toast-title" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
type ToastDescriptionProps = ToastPrimitive.Description.Props;
export function ToastDescription(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("text-sm text-muted-foreground", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <ToastPrimitive.Description data-slot="toast-description" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
type ToastActionProps = ToastPrimitive.Action.Props;
export function ToastAction(props) {
  const $ = _c(12);
  let className;
  let render;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      render,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = render;
    $[3] = rest;
  } else {
    className = $[1];
    render = $[2];
    rest = $[3];
  }
  let t0;
  if ($[4] !== render) {
    t0 = render ?? <Button variant="outline" size="sm" />;
    $[4] = render;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  let t1;
  if ($[6] !== className) {
    t1 = cn("shrink-0", className);
    $[6] = className;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== rest || $[9] !== t0 || $[10] !== t1) {
    t2 = <ToastPrimitive.Action data-slot="toast-action" render={t0} className={t1} {...rest} />;
    $[8] = rest;
    $[9] = t0;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}
type ToastCloseProps = ToastPrimitive.Close.Props;
export function ToastClose(props) {
  const $ = _c(16);
  let children;
  let className;
  let render;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      children,
      render,
      ...rest
    } = props);
    $[0] = props;
    $[1] = children;
    $[2] = className;
    $[3] = render;
    $[4] = rest;
  } else {
    children = $[1];
    className = $[2];
    render = $[3];
    rest = $[4];
  }
  let t0;
  if ($[5] !== render) {
    t0 = render ?? <Button variant="ghost" size="icon-sm" />;
    $[5] = render;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  let t1;
  if ($[7] !== className) {
    t1 = cn("relative shrink-0 text-muted-foreground after:absolute after:-inset-2 after:content-[''] hover:text-foreground", className);
    $[7] = className;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== children) {
    t2 = children ?? <XIcon aria-hidden="true" />;
    $[9] = children;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== rest || $[12] !== t0 || $[13] !== t1 || $[14] !== t2) {
    t3 = <ToastPrimitive.Close data-slot="toast-close" aria-label="Close toast" render={t0} className={t1} {...rest}>{t2}</ToastPrimitive.Close>;
    $[11] = rest;
    $[12] = t0;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
}
type ToastIconProps = {
  type: string | undefined;
};
function ToastIcon(props) {
  const $ = _c(7);
  const {
    type
  } = props;
  let icon = null;
  if (type === "success") {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <CircleCheckIcon aria-hidden="true" />;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    icon = t0;
  }
  if (type === "info") {
    let t0;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <InfoIcon aria-hidden="true" />;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    icon = t0;
  }
  if (type === "warning") {
    let t0;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <TriangleAlertIcon aria-hidden="true" />;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    icon = t0;
  }
  if (type === "error") {
    let t0;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <OctagonXIcon className="text-destructive" aria-hidden="true" />;
      $[3] = t0;
    } else {
      t0 = $[3];
    }
    icon = t0;
  }
  if (type === "loading") {
    let t0;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <Spinner aria-hidden="true" />;
      $[4] = t0;
    } else {
      t0 = $[4];
    }
    icon = t0;
  }
  if (!icon) {
    return null;
  }
  let t0;
  if ($[5] !== icon) {
    t0 = <span data-slot="toast-icon" className="shrink-0 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4">{icon}</span>;
    $[5] = icon;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  return t0;
}
function ToastList() {
  const $ = _c(2);
  const {
    toasts
  } = ToastPrimitive.useToastManager();
  let t0;
  if ($[0] !== toasts) {
    t0 = toasts.map(_temp);
    $[0] = toasts;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function _temp(toastItem) {
  return <Toast key={toastItem.id} toast={toastItem} data-testid={toastItem.data?.testId}><ToastContent><ToastIcon type={toastItem.type} /><div className="flex min-w-0 flex-1 flex-col gap-1"><ToastTitle /><ToastDescription /></div><ToastAction /><ToastClose /></ToastContent></Toast>;
}
type ToasterProps = ToastPrimitive.Provider.Props;
export function Toaster(props) {
  const $ = _c(9);
  let children;
  let rest;
  let t0;
  if ($[0] !== props) {
    ({
      children,
      toastManager: t0,
      ...rest
    } = props);
    $[0] = props;
    $[1] = children;
    $[2] = rest;
    $[3] = t0;
  } else {
    children = $[1];
    rest = $[2];
    t0 = $[3];
  }
  const toastManager = t0 === undefined ? toast : t0;
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ToastPortal><ToastViewport><ToastList /></ToastViewport></ToastPortal>;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== children || $[6] !== rest || $[7] !== toastManager) {
    t2 = <ToastProvider toastManager={toastManager} {...rest}>{children}{t1}</ToastProvider>;
    $[5] = children;
    $[6] = rest;
    $[7] = toastManager;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
export const createToastManager = ToastPrimitive.createToastManager;
export const useToastManager = ToastPrimitive.useToastManager;